import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    clearAppointmentsAndSlotsByUserAndDateRange,
    clearUserEventsByUserAndDateRange,
    searchAppointmentsAndSlotsByUserAndDateRange,
    searchUserEventsByUserAndDateRange
} from '../../../store/calendarService';
import {
    clearCourseModulesByUserAndDateRange,
    clearNewScheduledModules,
    loadCourse,
    rescheduleCourseModule,
    searchCourseModulesByUserAndDateRange,
    setCurrentModule,
    setOpenModuleSchedulerPanel
} from '../../../store/courseService';
import { clearUsersBySearch, loadUserById } from '../../../store/userService';
import { todayDate } from '../../../utils/dateFunctions';
import { hasRole } from '../../../utils/userRoles';
import Calendar from '../calendar/Calendar';
import AdviserSelect from '../components/AdviserSelect';
import {
    configCourseEventContent,
    configUserEventContent,
    fetchUserServiceIds,
    formatFullCalendarTimes,
    formatSlots,
    getBusinessHours,
    getServiceIds,
    mapColors,
    mapCommonParams,
    mapUserEventColors,
    moduleAccessRoles
} from '../utils/calendarUtils';

import ModuleScheduler from './ModuleScheduler';

import '../styles/calendarVendorClasses.css';
import app from '../../../app.module.css';

const _DOUBLE_CLICK = 300;
let userClickCount = 0;

const CourseCalendar = () => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();

    // LOCAL STATE
    const [range, setRange] = useState({
        dateFrom: todayDate(),
        dateTo: todayDate()
    });

    const [loggedInAdviser, setLoggedInAdviser] = useState({});
    const [usersServiceIds, setUsersServiceIds] = useState([]);
    const [adviser, setAdviser] = useState({});

    const [events, setEvents] = useState([]);
    const [newEvent, setNewEvent] = useState({});
    const [currentEvent, setCurrentEvent] = useState({});
    const [appointmentEvents, setAppointmentEvents] = useState([]);
    const [userEvents, setUserEvents] = useState([]);

    const [currentModules, setCurrentModules] = useState([]);
    const [currentUserEvents, setCurrentUserEvents] = useState([]);
    const [currentAppointments, setCurrentAppointments] = useState([]);

    const [pendingModule, setPendingModule] = useState({});
    const [changedModule, setChangedModule] = useState(false);

    const [isSlotSelectable, setIsSlotSelectable] = useState(false);
    const [isActionsDisabled, setIsActionsDisabled] = useState(true);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const usersByLoggedInUserServiceIds = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIds
    );
    const usersById = useSelector((state) => state.entities.userService.usersById);
    const users = useSelector((state) => state.entities.userService.usersBySearch);

    // modules
    const courseModules = useSelector(
        (state) => state.entities.courseService.courseModulesByUserAndDateRange
    );
    const courseModulesMetaData = useSelector(
        (state) => state.entities.courseService.courseModulesByUserAndDateRangeMetaData
    );
    const loadingModules = useSelector(
        (state) => state.entities.courseService.loadingCourseModulesByUserAndDateRange
    );
    const newScheduledModules = useSelector(
        (state) => state.entities.courseService.newScheduledModules
    );
    const currentCourse = useSelector((state) => state.entities.courseService.currentCourse);
    const currentModule = useSelector((state) => state.entities.courseService.currentModule);

    // user appointments
    const usersAppointments = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateRange
    );
    const usersAppointmentsMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateRangeMetaData
    );
    const loadingAppointments = useSelector(
        (state) => state.entities.calendarService.loadingUsersAppointmentsAndSlots
    );

    // user events
    const userEventsByUser = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateRange
    );
    const userEventsMetaData = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateRangeMetaData
    );
    const loadingUserEvents = useSelector(
        (state) => state.entities.calendarService.loadingUserEventsByUserAndDateRange
    );
    const openModuleScheduler = useSelector(
        (state) => state.entities.courseService.openModuleScheduler
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // CONFIG FNS
    const configEvents = () => {
        const businessHours = getBusinessHours();

        const newModules = adviser.id in newScheduledModules ? newScheduledModules[adviser.id] : [];
        let modules = [...currentModules, ...newModules];

        if (currentModule?.id) {
            const found = modules?.find((el) => el.id === currentModule?.id);
            if (!found) modules = [...modules, currentModule];
        }
        let entries;
        if (!modules?.length) entries = [{}];
        else
            entries = modules?.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const content = configCourseEventContent(el);
                const { color, textColor } = mapColors(el.id, currentModule?.id);
                const editable = el.id === currentModule?.id;
                return {
                    id: el.id,
                    eventType: 'courseModule',
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    content,
                    location: el.location,
                    date,
                    start,
                    end,
                    color,
                    textColor,
                    editable,
                    constraint: businessHours
                };
            });
        setEvents(entries);
        const cur = entries?.find((el) => el.id === currentModule?.id);
        if (cur) setCurrentEvent(cur);
    };

    const configBackgroundAppointmentEvents = () => {
        let events;
        if (currentAppointments?.length < 1) events = [];
        else
            events = currentAppointments?.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { slots, filledSlots } = formatSlots(el);

                return {
                    eventType: 'participant',
                    className: 'non-interactive-events',
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    slots,
                    filledSlots,
                    location: el.location,
                    appointmentType: el.type,
                    editable: false,
                    date,
                    start,
                    end
                };
            });
        setAppointmentEvents(events);
    };

    const configBackgroundUserEvents = () => {
        if (!Array.isArray(currentUserEvents)) return;
        let events;
        if (currentUserEvents.length < 1) events = [];
        else
            events = currentUserEvents.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { color, textColor } = mapUserEventColors(el.id);
                const { displayOccurrence, content } = configUserEventContent(el);
                return {
                    id: el.id,
                    eventType: 'userEvent',
                    className: 'non-interactive-events',
                    content,
                    timeContent: `${start.slice(-5)} - ${end.slice(-5)}`,
                    color,
                    textColor,
                    date,
                    start,
                    end,
                    editable: false,
                    repeat: el.repeat,
                    repeatUntilDate: el.repeatUntilDate,
                    displayOccurrence
                };
            });
        setUserEvents(events);
    };

    // USE EFFECTS
    useEffect(() => {
        return () => cleanUp();
    }, []);

    useEffect(() => {
        setIsActionsDisabled(!hasRole(moduleAccessRoles, loggedInUser.roles));
    }, [loggedInUser.roles]);

    useEffect(() => {
        if (adviser?.id) return;
        let user;
        if (currentModule?.userId) {
            user =
                usersById[currentModule.userId] ||
                usersByLoggedInUserServiceIds.find((el) => el.id === currentModule.userId);
        } else {
            if (!loggedInUser?.id || !usersByLoggedInUserServiceIds) return;
            user =
                usersByLoggedInUserServiceIds.find((el) => el.id === loggedInUser.id) ||
                loggedInUser;
        }
        if (user) {
            setAdviser(user);
            setLoggedInAdviser(user);
            const serviceIds = fetchUserServiceIds(user, dispatch);
            setUsersServiceIds(serviceIds);
        } else dispatch(loadUserById(currentModule.userId));
    }, [loggedInUser.id, usersByLoggedInUserServiceIds, usersById, currentModule.id]);

    useEffect(() => {
        if (!('id' in adviser)) return;
        fetchNewData(adviser.id);
    }, [adviser?.id, range]);

    useEffect(() => {
        if (currentModule?.userId && usersById[currentModule.userId]) {
            setAdviser(usersById[currentModule.userId] || {});
        }
    }, [usersById]);

    useEffect(() => {
        if (!currentModule?.id) setCurrentEvent({});
        if (currentModule?.id && !currentModule?.date) setIsSlotSelectable(true);
        configEvents();
    }, [currentModule]);

    // Modules loading - page + 1
    useEffect(() => {
        if (!('id' in adviser)) return;
        if (
            !loadingModules &&
            !!courseModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in courseModulesMetaData &&
            !courseModulesMetaData.last
        ) {
            dispatch(
                searchCourseModulesByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    courseModulesMetaData.number + 1
                )
            );
        } else if (courseModulesMetaData.last)
            setCurrentModules(
                courseModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
    }, [
        courseModules[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        courseModulesMetaData,
        loadingModules
    ]);

    // Users appointments loading - page + 1
    useEffect(() => {
        if (!('id' in adviser)) return;
        if (
            !loadingAppointments &&
            !!usersAppointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in usersAppointmentsMetaData &&
            !usersAppointmentsMetaData.last
        ) {
            dispatch(
                searchAppointmentsAndSlotsByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    usersAppointmentsMetaData.number + 1
                )
            );
        } else if (usersAppointmentsMetaData?.last) configAppointments();
    }, [
        loadingAppointments,
        usersAppointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        usersAppointmentsMetaData
    ]);

    // UserEvents loading - page + 1
    useEffect(() => {
        if (
            !loadingUserEvents &&
            !!userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`]?.length &&
            'number' in userEventsMetaData &&
            !userEventsMetaData.last
        ) {
            dispatch(
                searchUserEventsByUserAndDateRange(
                    {
                        userIds: [adviser.id],
                        dateFrom: range.dateFrom,
                        dateTo: range.dateTo
                    },
                    userEventsMetaData.number + 1
                )
            );
        } else if (userEventsMetaData?.last) {
            setCurrentUserEvents(
                userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }
    }, [
        userEventsByUser[`${adviser.id}_${range.dateFrom}_${range.dateTo}`],
        loadingUserEvents,
        userEventsMetaData
    ]);

    useEffect(() => {
        configEvents();
    }, [currentModules, newScheduledModules[adviser.id]]);

    useEffect(() => {
        configBackgroundAppointmentEvents();
    }, [currentAppointments]);

    useEffect(() => {
        configBackgroundUserEvents();
    }, [currentUserEvents]);

    useEffect(() => {
        if (changedModule) {
            onOpenModuleSchedulerPanel();
            setChangedModule(false);
        }
    }, [changedModule]);

    useEffect(() => {
        if (successMessage === `Module ${currentModule.title} has been rescheduled`) {
            dispatch(loadCourse(currentModule.courseId));
            dispatch(setCurrentModule({}));
        }
    }, [successMessage]);

    // HELPER FNS
    const fetchNewData = (adviserId) => {
        if (!adviserId) return;
        const params = {
            userIds: [adviserId],
            dateFrom: range.dateFrom,
            dateTo: range.dateTo
        };

        if (!(`${adviserId}_${range.dateFrom}_${range.dateTo}` in courseModules))
            dispatch(searchCourseModulesByUserAndDateRange(params, 0));
        else
            setCurrentModules(
                courseModules[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );

        if (!(`${adviserId}_${range.dateFrom}_${range.dateTo}` in usersAppointments))
            dispatch(searchAppointmentsAndSlotsByUserAndDateRange(params, 0));
        else {
            setCurrentAppointments(
                usersAppointments[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }

        if (!(`${adviserId}_${range.dateFrom}_${range.dateTo}` in userEventsByUser))
            dispatch(searchUserEventsByUserAndDateRange(params, 0));
        else {
            setCurrentUserEvents(
                userEventsByUser[`${adviserId}_${range.dateFrom}_${range.dateTo}`] || []
            );
        }
    };

    const configAppointments = () => {
        if (!('id' in adviser)) return;
        setCurrentAppointments(
            usersAppointments[`${adviser.id}_${range.dateFrom}_${range.dateTo}`] || []
        );
    };

    const clearCurrentEventData = () => {
        setCurrentAppointments([]);
        setCurrentUserEvents([]);
        setCurrentModules([]);
        setCurrentEvent({});
        setPendingModule({});
    };

    const cleanUp = () => {
        dispatch(clearAppointmentsAndSlotsByUserAndDateRange());
        dispatch(clearNewScheduledModules());
        dispatch(clearUserEventsByUserAndDateRange());
        dispatch(clearCourseModulesByUserAndDateRange());
        dispatch(setCurrentModule({}));
        dispatch(clearUsersBySearch());
    };

    // EVENT HANDLERS
    const onDragEditTimes = (time, update) => {
        if (update) {
            const { date, startTime, endTime } = formatFullCalendarTimes(time);
            if (currentEvent.eventType === 'courseModule') {
                const updatedModule = {
                    ...currentModule,
                    date,
                    startTime,
                    endTime,
                    userId: adviser.id
                };
                dispatch(rescheduleCourseModule(updatedModule));
            }
        }
        dispatch(setCurrentModule({}));
    };

    const onOpenModuleSchedulerPanel = () => {
        if (isSlotSelectable) {
            dispatch(setOpenModuleSchedulerPanel(true));
        }
        if (!isSlotSelectable) {
            userClickCount++;
            const timeoutId = setTimeout(() => (userClickCount = 0), _DOUBLE_CLICK);
            if (userClickCount > 1) {
                dispatch(setOpenModuleSchedulerPanel(true));
                setNewEvent({});
                setPendingModule({});
            }
            return () => clearTimeout(timeoutId);
        }
    };

    const onSelectEvent = (id) => {
        setIsSlotSelectable(false);
        if (id !== currentModule?.id) {
            const newModules =
                adviser.id in newScheduledModules ? newScheduledModules[adviser.id] : [];
            const selectedModule = [...currentModules, ...newModules].find((el) => el.id === id);
            selectedModule && dispatch(setCurrentModule(selectedModule));
            setChangedModule(true);
        }
        if (id === currentEvent.id) onOpenModuleSchedulerPanel();
    };

    const onSelectSlot = (start, end) => {
        if (!isSlotSelectable) return;
        // this is only on redirect from schedule module
        const { date, startTime, endTime } = formatFullCalendarTimes({ start, end });
        if (Object.keys(pendingModule).length)
            setNewEvent({ ...pendingModule, date, startTime, endTime });
        else setNewEvent({ ...currentModule, date, startTime, endTime });
        onOpenModuleSchedulerPanel();
    };

    const onGetCurrentTimeSpan = (dateFrom, dateTo, view) => {
        setRange({
            dateFrom,
            dateTo: view === 'timeGridDay' ? dateFrom : dateTo
        });
    };

    const onAdviserChange = (chosenId) => {
        if (!chosenId) {
            if (adviser.id !== loggedInUser.id) setAdviser(loggedInAdviser);
        } else if (chosenId !== adviser.id) {
            clearCurrentEventData();
            const chosenAdviser = [...users, loggedInAdviser].find((el) => el.id === chosenId);
            if (chosenAdviser) {
                setAdviser({
                    ...chosenAdviser,
                    contractIds: chosenAdviser.contracts || chosenAdviser.contractIds,
                    serviceIds: getServiceIds(chosenAdviser)
                });
            }
        }
    };

    const onReturn = () => {
        if (state?.prevPage === 'editCourse')
            navigate('/edit_course', { state: { currentCourse } });
        else navigate('/course_management');
    };

    const onPending = (unScheduledModule) => setPendingModule(unScheduledModule);

    // RENDER
    return (
        <div className={app.container}>
            <div className="calendar-wrapper">
                <AdviserSelect
                    userId={adviser.id}
                    serviceIds={usersServiceIds}
                    initialAdviser={loggedInAdviser}
                    onAdviserChange={onAdviserChange}
                    disabled={isActionsDisabled}
                    customClass="calendarToolbarAdviserSelect"
                />

                <Calendar
                    type="courseCalendar"
                    events={[...events, ...appointmentEvents, ...userEvents]}
                    currentEvent={currentEvent}
                    onDragEditTimes={onDragEditTimes}
                    onSelectSlot={
                        isSlotSelectable && currentModule?.title ? onSelectSlot : undefined
                    }
                    onSelectEvent={onSelectEvent}
                    isActionsDisabled={isActionsDisabled}
                    initialView="timeGridWeek"
                    onGetCurrentTimeSpan={onGetCurrentTimeSpan}
                    onReturn={onReturn}
                />
                {openModuleScheduler && !isActionsDisabled && adviser?.id && (
                    <ModuleScheduler
                        newEvent={newEvent}
                        currentModule={currentModule}
                        onPending={onPending}
                        adviser={adviser}
                    />
                )}
            </div>
        </div>
    );
};

export default CourseCalendar;
